/**
 * Showing a text file in the chat instead of only offering to save it.
 *
 * A twin of the phone's rules, kept in the same shape for the same reason the rest of the attachment reading
 * is: the two clients read the same messages, and a file that is read in place on one and a download link on
 * the other is the same file described two ways.
 *
 * Three rules decide what is shown. It has to be a KIND of file that is text AND its bytes have to actually
 * BE text — a mime says what the sender's computer guessed, so a binary labelled `text/plain` would render as
 * replacement characters, and bytes alone would preview the readable head of a document format. It is
 * BOUNDED, because a log dropped into a bubble in full is a scroll between the reader and the conversation.
 * And the extension names the language, so the listing is highlighted by what already highlights the chat's
 * fenced blocks, and one nothing recognises is shown plain.
 */

export interface TextPreview {
  /** The text as shown — already bounded. */
  text: string;
  /** What to highlight it as, or undefined to leave it plain. */
  lang?: string;
  /** Whether anything was left out of `text`. */
  truncated: boolean;
}

/** How much of a text file is shown before the reader is asked to open it properly. */
const PREVIEW_CHARS = 1400;
const PREVIEW_LINES = 20;
/** How much of a file is read to decide whether it is text at all. */
const SNIFF_BYTES = 4096;

/** Extensions whose contents are text worth reading in place, mapped to what the listing is in. */
const TEXT_EXTENSIONS: Record<string, string | undefined> = {
  txt: undefined, log: undefined, text: undefined, csv: undefined, tsv: undefined, env: undefined,
  md: undefined, markdown: undefined, rst: undefined,
  json: "json", yaml: "yaml", yml: "yaml", toml: undefined, ini: "ini", conf: undefined,
  properties: "properties", xml: "xml", html: "xml", css: "css", sql: "sql",
  kt: "kotlin", kts: "kotlin", java: "java", gradle: "kotlin",
  ts: "typescript", tsx: "typescript", js: "javascript", jsx: "javascript", mjs: "javascript",
  py: "python", sh: "bash", bash: "bash", zsh: "bash",
  diff: "diff", patch: "diff",
};

function extensionOf(name?: string): string {
  const dot = name?.lastIndexOf(".") ?? -1;
  if (dot < 0) return "";
  const ext = name!.slice(dot + 1).toLowerCase();
  return ext.length > 0 && ext.length <= 10 ? ext : "";
}

/**
 * Whether a file of this name and mime holds text.
 *
 * The name is asked first: it is what a person chose, and an extension says what the file IS where a mime
 * often says only that nothing recognised it. A mime is accepted alone for the families that are text by
 * definition, which is how a file with no extension can still be read here.
 */
export function looksTextual(name?: string, mime?: string): boolean {
  // Own keys only: every object answers to a handful of inherited names, and a file called after one of them
  // would be previewed on the strength of the prototype chain.
  if (Object.hasOwn(TEXT_EXTENSIONS, extensionOf(name))) return true;
  const m = mime?.toLowerCase().split(";")[0]?.trim();
  if (!m) return false;
  if (m.startsWith("text/")) return true;
  return m === "application/json" || m === "application/xml" || m.endsWith("+json") || m.endsWith("+xml");
}

/**
 * The preview to show under a file row, or null when there is nothing to show in place.
 *
 * Null covers every case where the row should stay the row it was: a file of a kind that is not text, one
 * whose bytes are not text after all, and an empty one — a heading over nothing reads as a preview that
 * failed rather than as a file with no content in it.
 */
export function textPreview(
  bytes: Uint8Array | null | undefined,
  name?: string,
  mime?: string,
  maxChars = PREVIEW_CHARS,
  maxLines = PREVIEW_LINES,
): TextPreview | null {
  if (!bytes || bytes.length === 0) return null;
  if (!looksTextual(name, mime)) return null;
  // Decoded strictly, from a bounded head: what a lenient decoder does with bytes that are not text is
  // exactly the mojibake this is meant to keep out of the chat.
  let full: string;
  try {
    const decoder = new TextDecoder("utf-8", { fatal: true });
    decoder.decode(bytes.subarray(0, sniffEnd(bytes)));
    full = new TextDecoder("utf-8").decode(bytes);
  } catch {
    return null;
  }
  // A NUL is what no text file contains and most binaries do, and it decodes without complaint.
  if (full.slice(0, SNIFF_BYTES).includes("\u0000")) return null;
  const trimmed = full.replace(/[\r\n]+$/, "");
  if (trimmed.trim().length === 0) return null;

  const lines = trimmed.split("\n");
  let text = lines.length > maxLines ? lines.slice(0, maxLines).join("\n") : trimmed;
  let truncated = lines.length > maxLines;
  if (text.length > maxChars) {
    text = text.slice(0, maxChars).replace(/\s+$/, "");
    truncated = true;
  }
  const ext = extensionOf(name);
  const lang = Object.hasOwn(TEXT_EXTENSIONS, ext) ? TEXT_EXTENSIONS[ext] : undefined;
  return { text, ...(lang ? { lang } : {}), truncated };
}

/**
 * Where the bounded read may stop without cutting a character in half.
 *
 * The strict decoder that decides "are these bytes text" rejects a character it does not see the end of — so
 * a cut landing in the middle of a multi-byte one refuses a perfectly good file its preview, over where its
 * accented letter happened to fall. Every byte of the middle of a character has its top two bits set to one
 * and zero, which is what is walked back from here.
 */
function sniffEnd(bytes: Uint8Array): number {
  if (bytes.length <= SNIFF_BYTES) return bytes.length;
  let end = SNIFF_BYTES;
  while (end > 0 && (bytes[end]! & 0xc0) === 0x80) end--;
  return end;
}

/**
 * The preview as a fenced block, for the renderer that already draws the chat's listings.
 *
 * The fence is longer than the longest run of backticks the text contains: a file holding a fence of its own
 * would otherwise close the block early and spill the rest of itself into the bubble as markup.
 */
export function fencedFor(preview: TextPreview): string {
  const longest = (preview.text.match(/`+/g) ?? []).reduce((n, run) => Math.max(n, run.length), 0);
  const fence = "`".repeat(Math.max(3, longest + 1));
  return `${fence}${preview.lang ?? ""}\n${preview.text}\n${fence}`;
}
